import fetchDemoMaterialProducts from 'app/apis/fetchDemoMaterialProducts'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { DemoMaterialProduct } from 'app/models/RequestFlow'
import { useCallback, useEffect, useRef, useState } from 'react'

const mergeProductPages = (
  prev: DemoMaterialProduct[],
  incoming: DemoMaterialProduct[],
) => {
  if (!incoming.length) return prev
  const existingIds = new Set(prev.map(item => item.id))
  const uniqueIncoming = incoming.filter(item => !existingIds.has(item.id))
  if (!uniqueIncoming.length) return prev
  return [...prev, ...uniqueIncoming]
}

const useFetchDemoMaterialProducts = ({
  name,
  brand,
}: {
  name?: string
  brand?: string
}) => {
  const [products, setProducts] = useState<DemoMaterialProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingNext, setLoadingNext] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isFetchingNextRef = useRef(false)
  const pageRef = useRef(1)
  const hasMoreRef = useRef(false)

  const fetchPage = useCallback(
    async (page: number) => fetchDemoMaterialProducts(page, 10, name, brand),
    [name, brand],
  )

  const getRequest = useCallback(async () => {
    setLoading(true)
    setError(null)
    setProducts([])
    setHasMore(false)
    hasMoreRef.current = false
    pageRef.current = 1
    isFetchingNextRef.current = false
    try {
      const response = await fetchPage(1)
      if (response?.response?.status && response?.response?.status === 400) {
        setError('Something went wrong')
        ToastAndroid.show(response.response.data.detail, ToastAndroid.SHORT)
        setProducts([])
      } else if (Array.isArray(response?.results)) {
        setProducts(response.results)
        const more = Boolean(response.next)
        setHasMore(more)
        hasMoreRef.current = more
      } else {
        setError('Something went wrong')
        setProducts([])
        if (response?.detail) {
          ToastAndroid.show(response.detail, ToastAndroid.SHORT)
        } else {
          ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
        }
      }
    } catch {
      setError('Something went wrong')
      setProducts([])
      ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
    } finally {
      setLoading(false)
    }
  }, [fetchPage])

  const fetchNext = useCallback(async () => {
    if (!hasMoreRef.current || isFetchingNextRef.current) return false

    try {
      isFetchingNextRef.current = true
      setLoadingNext(true)
      const nextPage = pageRef.current + 1
      const response = await fetchPage(nextPage)
      if (Array.isArray(response?.results)) {
        pageRef.current = nextPage
        setProducts(prev => mergeProductPages(prev, response.results))
        const more = Boolean(response.next)
        setHasMore(more)
        hasMoreRef.current = more
        return true
      }
      setError('Something went wrong')
      setHasMore(false)
      hasMoreRef.current = false
      if ((response as { detail?: string })?.detail) {
        ToastAndroid.show((response as { detail: string }).detail, ToastAndroid.SHORT)
      } else {
        ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
      }
      return false
    } catch {
      setError('Something went wrong')
      ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
      return false
    } finally {
      isFetchingNextRef.current = false
      setLoadingNext(false)
    }
  }, [fetchPage])

  useEffect(() => {
    getRequest()
  }, [getRequest])

  return {
    products,
    loading,
    loadingNext,
    hasMore,
    error,
    fetchNext,
    refetch: getRequest,
  }
}

export default useFetchDemoMaterialProducts
